<template>
    <div ref="top">
        <ui-card class="pt-4 pl-4 pr-6">

            <p v-if="loading"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Please wait. Loading application...</p>

            <div class="columns is-gapless mb-6" v-else>
                <div class="column is-narrow is-size-5"><i class="fad fa-fw fa-2x has-text-primary mr-5" :class="{'fa-file-certificate': application.type === 'application', 'fa-file-invoice': application.type === 'form'}"></i></div>
                <div class="column">
                    <div class="has-text-weight-bold is-size-5 mt-1">{{ application.title }}</div>
                    <div class="is-size-6 has-text-grey" v-if="application.type === 'application'">{{ application.cert.certification.title }}</div>

                    <!-- in progress -->
                    <section v-if="enrollApplication.progress <= application.outline.length && !enrollApplication.completed_at && !enrollApplication.is_in_review && !payment">
                        <div class="is-size-6 mt-3"><strong>Step {{ enrollApplication.progress }} of {{ lastStepCount }}</strong>: {{ component.title }}</div>

                        <!-- Reviewer hand-back (same block the conference application shows);
                             review_state comes from the latest reviewer log entry -->
                        <b-message type="is-warning" v-if="enrollApplication.review_state === 'changes_requested'" class="mt-5">
                            <div class="columns is-gapless">
                                <div class="column is-narrow">
                                    <i class="far fa-fw fa-exclamation-triangle mr-2"></i>
                                </div>
                                <div class="column">
                                    <div class="mb-3 has-text-weight-bold">Changes Requested on Your Application</div>
                                    <div v-if="enrollApplication.review_message" class="review-message">{{ enrollApplication.review_message }}</div>
                                    <div v-else>The reviewer sent this step back to you. Update it and submit again.</div>
                                </div>
                            </div>
                        </b-message>

                        <hr class="mt-5 mb-6">

                        <render-blocks :blocks="content" :data="enrollApplication.application_data" :enrollment="combinedEnrollment" :component="component" @saveData="saveData"></render-blocks>

                        <hr class="mt-6">

                        <section v-if="component && component.content && component.content.attributes.require_signature === '1'">
                            <signature :user="currentUser" :component="component" @input="saveSig"></signature>
                        </section>

                        <div class="mt-6">
                            <b-button @click="submit" size="is-large" type="is-info" expanded :disabled="isNextDisabled">Next Step<i class="fal fa-arrow-right-long ml-3"></i></b-button>
                        </div>
                    </section>

                    <!-- in review -->
                    <section v-else-if="enrollApplication.is_in_review">
                        <div class="is-size-6 mt-3"><strong>Step {{ enrollApplication.progress }} of {{ lastStepCount }}</strong>: {{ component.title }} <strong class="has-text-warning">(In Review)</strong></div>
                        <hr class="mt-5 mb-6">
                        <b-message type="is-success">
                            <p>The step you just completed in your application requires approval to continue. During this time your information will be reviewed and checked for accuracy. This process can take up to 48 hours. You will receive an email to {{ currentUser.email }} when your application has been processed and you can continue the application process.</p>
                            <div class="mt-4 pr-6 has-text-weight-bold">
                                <i class="far fa-fw fa-arrow-right mr-2"></i>Please, check back later!
                            </div>
                        </b-message>
                    </section>

                    <!-- payment -->
                    <section v-else-if="payment">
                        <div class="is-size-6 mt-3"><strong>Step {{ enrollApplication.progress }} of {{ lastStepCount }}</strong>: Payment</div>
                        <hr class="mt-5 mb-6">

                        <b-message type="is-success">
                            <p>Your application has been approved and is complete. A fee is required to award this certification. Please enter your card information below to continue.</p>
                        </b-message>

                        <div v-if="chargeAmount > 0">
                            <b-message type="is-warning" class="mt-6">
                                <div class="pr-6">
                                    <i class="far fa-fw fa-arrow-right mr-2"></i>Your card will be charged <strong>{{ chargeAmount | currency }}</strong>.
                                </div>
                            </b-message>

                            <!-- Tax breakdown -->
                            <div v-if="taxAmount > 0" class="mt-4 mb-4" style="background-color: #f8f9fa; padding: 1rem; border-radius: 4px; border: 1px solid #e0e2e8;">
                                <table style="width: 100%; font-size: 0.95rem; border-collapse: collapse;">
                                    <tr>
                                        <td style="padding-bottom: 0.25rem; border: none;">Application Fee:</td>
                                        <td style="text-align: right; padding-bottom: 0.25rem; border: none;">{{ baseAmount | currency }}</td>
                                    </tr>
                                    <tr v-if="taxDetails">
                                        <td style="padding-bottom: 0.25rem; border: none;">Sales Tax ({{ (taxDetails.taxRate * 100).toFixed(2) }}%, {{ taxDetails.jurisdiction }}):</td>
                                        <td style="text-align: right; padding-bottom: 0.25rem; border: none;">{{ taxAmount | currency }}</td>
                                    </tr>
                                    <tr v-else>
                                        <td style="padding-bottom: 0.25rem; border: none;">Sales Tax:</td>
                                        <td style="text-align: right; padding-bottom: 0.25rem; border: none;">{{ taxAmount | currency }}</td>
                                    </tr>
                                    <tr style="font-weight: 600;">
                                        <td style="padding-top: 0.5rem; border: none; border-top: 1px solid #dee2e6;">Total:</td>
                                        <td style="text-align: right; padding-top: 0.5rem; border: none; border-top: 1px solid #dee2e6;">{{ chargeAmount | currency }}</td>
                                    </tr>
                                </table>
                            </div>

                            <div v-if="!clientSecret" class="pb-3"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Establishing secure connection for Credit Card...</div>

                            <div v-show="clientSecret" class="card-container">
                                <div ref="card"></div>
                                <b-message type="is-danger" v-show="stripeErrors" class="mt-6">
                                    <div id="card-errors"><i class="far fa-exclamation-triangle mr-3"></i>{{ stripeErrors }}</div>
                                </b-message>
                            </div>

                            <div class="mt-6">
                                <b-button @click="runPayment" size="is-large" type="is-info" expanded :disabled="!ccReady || processing"><strong>Pay {{ chargeAmount | currency }}</strong> & Continue<i class="fal fa-arrow-right-long ml-3"></i></b-button>
                            </div>
                        </div>
                        <div class="mt-6" v-else>
                            <b-button @click="runPayment" size="is-large" type="is-info" expanded>Next Step<i class="fal fa-arrow-right-long ml-3"></i></b-button>
                        </div>
                    </section>

                    <!-- complete -->
                    <section v-else>
                        <div class="is-size-6 mt-3"><strong>Step {{ lastStepCount }} of {{ lastStepCount }}</strong>: Complete</div>
                        <hr class="mt-5 mb-6">

                        <!-- Completing this application may have been the course's last
                             outstanding requirement (completion redesign trigger 2) -->
                        <b-message type="is-success" v-if="courseCompleted" class="mb-6">
                            <p class="has-text-weight-bold is-size-5"><i class="fas fa-circle-check mr-2"></i>Course Complete!</p>
                            <p class="mt-3">Congratulations &mdash; completing this {{ application.type }} finished the last requirement of your course. Your course is now complete, and your certificates and completion forms are available below and in <strong style="white-space: nowrap;"><i class="fad fa-file-certificate mr-2"></i>Certifications &amp; Forms</strong>.</p>
                        </b-message>

                        <template v-if="application.type === 'application'">
                            <div v-for="award in application.cert.awards" class="award">

                                <div class="columns is-gapless">
                                    <div class="column is-narrow"><i class="fad fa-fw fa-certificate fa-2x has-text-primary mr-3"></i></div>
                                    <div class="column">
                                        <div class="has-text-weight-bold is-size-5">{{ award.title }}</div>
                                        <div class="mt-2">{{ award.description }}</div>
                                        <div class="mt-3 has-text-grey is-size-7">
                                            <div><strong class="">Approved: </strong>&nbsp;{{ enrollApplication.completed_at | moment('MMMM Do, YYYY') }}</div>
                                            <div><strong>Provider: </strong>&nbsp;{{ application.organization.title }}</div>
                                            <div v-if="application.cert.certification.type === 'unit'"><strong>{{ application.cert.certification.unit_title }}:</strong> {{ enrollApplication.total_units }}</div>
                                        </div>

                                        <template v-if="award.type === 'PDF'">
                                            <hr class="mt-5">

                                            <div class="mt-6">
                                                <b-button
                                                    tag="a"
                                                    target="_blank"
                                                    size="is-large"
                                                    type="is-info"
                                                    expanded
                                                    :href="apiUrl + 'app/enrollments/' + enrollmentId + '/applications/' + applicationId + '/pdf/' + award.key"
                                                ><i class="fas fa-file-pdf mr-4"></i>{{ award.pdf_title }}
                                                </b-button>
                                            </div>
                                        </template>
                                        <template v-else-if="award.type === 'CERT'">

                                        </template>
                                    </div>
                                </div>

                                <hr class="mt-6 mb-6 award-divider">

                            </div>

                            <!-- Printable study log — only when the award was
                                 computed from logged study time (total_units set).
                                 No divider needed: rendering this block makes the last
                                 award's own award-divider visible (it is no longer
                                 :last-child), which separates the two. -->
                            <div v-if="enrollApplication.total_units">
                                <div class="columns is-gapless">
                                    <div class="column is-narrow"><i class="fad fa-fw fa-clipboard-list-check fa-2x has-text-primary mr-3"></i></div>
                                    <div class="column">
                                        <div class="has-text-weight-bold is-size-5">Printable Study Log</div>
                                        <div class="mt-2">A printable copy of this course's Study Log (snapshot). This is <strong>NOT</strong> a {{ application.cert.certification.unit_title || 'clock hour' }} form &mdash; it <strong>IS</strong> your record/activity/study log.</div>

                                        <div class="mt-6">
                                            <b-button
                                                tag="a"
                                                target="_blank"
                                                size="is-large"
                                                type="is-info"
                                                outlined
                                                expanded
                                                :href="printableLogUrl"
                                            ><i class="fas fa-print mr-4"></i>Printable Study Log
                                            </b-button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </template>

                        <template v-if="application.type === 'form'">
                            <div class="columns is-gapless">
                                <div class="column is-narrow">
                                    <i class="fas is-size-4 mr-4 fa-check has-text-success"></i>
                                </div>
                                <div class="column">
                                    <h4>Thank you, this form is complete.</h4>
                                    <div class="mt-5">You can return to <strong style="white-space: nowrap;"><i class="fad fa-file-certificate mr-2"></i>Certifications &amp; Forms</strong> or <strong style="white-space: nowrap;"><i class="fad fa-chalkboard-teacher mr-2"></i>Course Information</strong> from the menu.</div>

                                </div>
                            </div>

                            <!--                            <div class="mt-6">-->
                            <!--                                <router-link :to="{name: 'course-applications'}">-->
                            <!--                                    <b-button><i class="fas fa-arrow-left mr-3"></i>Certifications & Forms</b-button>-->
                            <!--                                </router-link>-->
                            <!--                                <router-link :to="{name: 'course-outline'}">-->
                            <!--                                    <b-button class="ml-2" type="is-primary">Continue Course<i class="fas fa-arrow-right ml-3"></i></b-button>-->
                            <!--                                </router-link>-->
                            <!--                            </div>-->
                        </template>
                    </section>

                </div>
            </div>

        </ui-card>
    </div>
</template>

<script>
    import {client as http, apiUrl, stripePK, verifySession} from "../../../http_client";
    import async from "async";
    import UiCard from "../../../components/UiCard.vue";

    import LessonComponent from "../../../components/course/LessonComponent";
    import QuizComponent from "../../../components/course/QuizComponent";
    import SurveyComponent from "../../../components/course/SurveyComponent";
    import CheckpointComponent from "../../../components/course/CheckpointComponent";
    import RenderBlocks from "../../../components/course/RenderBlocks.vue";
    import Signature from "./Signature.vue";
    import {mapActions, mapGetters} from "vuex";

    let stripe = Stripe(stripePK),
        elements = stripe.elements(),
        card = undefined;

    export default {
        components: {RenderBlocks, UiCard, LessonComponent, QuizComponent, CheckpointComponent, SurveyComponent, Signature},
        props: ['enrollmentId', 'applicationId'],
        data: function() {
            return {
                enrollApplication: {},
                courseApplication: {},
                application: {},
                loading: true,
                apiUrl,

                lastStepCount: 0,

                courseCompleted: false,

                signature: null,

                payment: false,
                stripeErrors: null, // stripe errors
                stripeInitialized: false,
                clientSecret: null,
                intentId: null,
                intentCharge: 0,
                ccReady: false,
                processing: false,
                taxAmount: 0,
                baseAmount: 0,
                taxDetails: null
            };
        },
        computed: {
            ...mapGetters(['currentUser', 'enrollment']),
            component: function() {
                if (this.application.id) {
                    let component;
                    let section = this.application.outline[this.enrollApplication.progress - 1];
                    if (section) {
                        component = section.component;
                    } else {
                        return [];
                    }
                    if (typeof component.content === 'string') component.content = JSON.parse(component.content);
                    return component;
                } else {
                    return null;
                }
            },
            content: function() {
                // TODO this is conflicting with the 'component' computed property so we have to see if its been parsed or not
                if (this.application.id) {
                    let component;
                    let section = this.application.outline[this.enrollApplication.progress - 1];
                    if (section) {
                        component = section.component;
                    } else {
                        return [];
                    }

                    if (typeof component.content === 'string') {
                        return component.content = JSON.parse(component.content).content;
                    } else {
                        return component.content.content;
                    }
                } else {
                    return [];
                }
            },
            isNextDisabled: function() {
                if (this.component.content.attributes.require_signature === '1') {
                    return !this.signature;
                } else {
                    return false;
                }
            },
            chargeAmount() {
                let charge = 0;
                // The server's apply-time quote is authoritative — it accounts
                // for per-enrollment unit-price overrides (bundles) that the
                // course_application prices don't reflect
                if (this.enrollApplication.quoted_price) {
                    charge = parseFloat(this.enrollApplication.quoted_price);
                } else if (this.courseApplication.flat_price) {
                    charge = this.courseApplication.flat_price;
                } else if (this.courseApplication.per_unit_price) {
                    charge = this.enrollApplication.total_units * this.courseApplication.per_unit_price;
                }

                if (this.taxAmount) {
                    charge += this.taxAmount;
                }

                return charge;
            },
            isRequiredComplete() {
                for (let i = 0; i < this.content.length; i++) {
                    const block = this.content[i];
                    if (block.content && block.content.required) {
                        const answer = this.enrollApplication.application_data[block.content.id];
                        if (!answer) {
                            return false;
                        }
                        if (block.component === 'DistrictSchoolField' && (!answer.district || !answer.school)) {
                            return false;
                        }
                    }
                }
                return true;
            },
            printableLogUrl: function() {
                // Server-rendered PDF — identical output in every browser
                // (client-side print CSS proved unreliable, notably in Safari)
                return apiUrl + 'app/enrollments/' + this.enrollmentId + '/applications/' + this.applicationId
                    + '/studylog.pdf?auth=' + encodeURIComponent(localStorage.getItem('token') || '');
            },
            combinedEnrollment: function() {
                return {
                    is_course_enrollment: true,
                    ...this.enrollment,
                    regApplication: this.enrollApplication, // I don't care for this var name
                    courseApplication: this.courseApplication,
                    application: this.application
                }
            }
        },
        methods: {
            ...mapActions(['getEnrollmentById']),
            load: function(force) {
                this.loading = true;
                async.series([
                    (cb) => {
                        http.get('/app/enrollments/' + this.enrollmentId + '/applications/' + this.applicationId, {force}).then(response => {
                            this.enrollApplication = response.data;
                            cb();
                        }).catch(err => {
                            console.log('enrollApplication error', err);
                            cb(err);
                        });
                    },
                    (cb) => {
                        http.get('/app/courses/' + this.enrollment.course_id + '/applications/' + this.enrollApplication.course_application_id, {force}).then(response => {
                            response.data.per_unit_price = parseFloat(response.data.per_unit_price);
                            response.data.flat_price = parseFloat(response.data.flat_price);
                            this.courseApplication = response.data;
                            cb();
                        }).catch(err => {
                            console.log('enrollApplication error', err);
                            cb(err);
                        });
                    },
                    (cb) => {
                        http.get('/app/applications/' + this.courseApplication.application_id, {force}).then(response => {
                            this.application = response.data;
                            // this.enrollment.application = this.application;
                            this.lastStepCount = this.application.outline.length + 1;
                            if (this.enrollApplication.quoted_price) this.lastStepCount++;

                            if (this.enrollApplication.progress > this.application.outline.length && this.enrollApplication.is_unpaid) {
                                console.log('payment');
                                this.payment = true;
                                this.getPaymentIntent();
                            }
                            cb();
                        }).catch(err => {
                            cb(err);
                        });
                    },
                ], err => {
                    if (err) {
                        console.log(err);
                    }

                    this.loading = false;
                });
            },
            submit: function() {
                if (!this.isRequiredComplete) {
                    this.$buefy.toast.open({
                        message: 'Please complete all required fields.',
                        type: 'is-danger',
                        duration: 5000,
                        position: 'is-top-right'
                    });
                    return;
                }
                if (!this.loading) {
                    this.loading = true;

                    http.post('/app/enrollments/' + this.enrollmentId + '/applications/' + this.applicationId + '/progress', {}, {force: true}).then(response => {
                        this.scrollTo('top');
                        if (response.data && response.data.course_completed) {
                            this.courseCompleted = true;
                        }
                        if (this.component.content.attributes.require_approval === '1') {
                            console.log('in review');
                            this.enrollApplication.is_in_review = 1;
                        } else if ((this.enrollApplication.progress + 1) === this.lastStepCount) {
                            console.log('complete');
                            this.enrollApplication.completed_at = new Date();

                            // Reload enrollment to update overall status, a bit lazy here...
                            this.getEnrollmentById(this.enrollmentId);

                        } else {
                            console.log('progress');
                            this.enrollApplication.progress++;
                        }

                        if (this.enrollApplication.progress > this.application.outline.length && this.enrollApplication.is_unpaid) {
                            console.log('payment');
                            this.payment = true;
                            this.getPaymentIntent();
                        }

                        this.loading = false;
                        this.processing = false;
                    }).catch(err => {
                        this.loading = false;
                        console.log(err);
                    });
                }
            },
            saveData: function(payload) {
                payload.enrollment_application_id = parseInt(this.applicationId);

                // TODO Parse JSON coming from the component.  Prisma will do it on the backend for this endpoint.
                try {
                    payload.block = JSON.parse(payload.block);
                } catch (e) {
                }
                try {
                    payload.value = JSON.parse(payload.value);
                } catch (e) {
                }

                http.post('/app/enrollments/' + this.enrollmentId + '/applications/' + this.applicationId + '/save', payload, {force: true}).then(response => {

                }).catch(err => {

                });
            },
            saveSig: function(data) {
                this.signature = data;
                this.saveData({
                    field: 'signature',
                    block: JSON.stringify(this.component.content.attributes),
                    value: JSON.stringify({signature: data})
                });
            },

            getPaymentIntent: function() {
                http.post('app/enrollments/' + this.enrollment.id + '/applications/intent', {id: this.applicationId}, {force: true}).then(response => {
                    this.intentId = response.data.id;
                    this.clientSecret = response.data.clientSecret;
                    this.baseAmount = response.data.baseAmount || 0;
                    this.taxAmount = response.data.taxAmount || 0;
                    this.taxDetails = response.data.taxDetails || null;

                    card = elements.create('card');
                    card.mount(this.$refs.card);

                    card.on('change', event => {
                        if (event.error) {
                            this.stripeErrors = event.error.message;
                        } else {
                            this.stripeErrors = '';
                        }

                        this.ccReady = !!(event.complete);
                    });
                }).catch(err => {
                    window.alert("I'm sorry, there was an error establishing a connection with the payment processor. Please try again later. If you continue to have this issue please contact support.");
                });
            },
            runPayment() {
                if (this.chargeAmount > 0) {
                    if (!this.processing) {
                        this.processing = true;

                        // Confirm the session is still alive BEFORE the card is charged —
                        // a tab left open past the JWT lifetime can still confirm the
                        // payment with Stripe but then 401 on the submit call below
                        verifySession().then(() => {
                            stripe.confirmCardPayment(this.clientSecret, {
                                payment_method: {
                                    card: card,
                                    billing_details: {
                                        name: this.currentUser.first_name + ' ' + this.currentUser.last_name,
                                        email: this.currentUser.email
                                    }
                                }
                            }).then(result => {
                                if (result.error) {
                                    this.stripeErrors = result.error.message;
                                    console.log(result.error.message);
                                    this.processing = false;
                                } else if (result.paymentIntent.status === 'succeeded') {
                                    console.log('Payment succeeded');
                                    this.enrollApplication.progress++;
                                    this.enrollApplication.is_unpaid = 0;
                                    this.enrollApplication.total_paid = this.chargeAmount;
                                    this.enrollApplication.completed_at = new Date();
                                    this.payment = false;
                                    this.submit();
                                }
                            });
                        }).catch(err => {
                            this.processing = false;
                            if (err.response && err.response.status === 401) {
                                window.alert('Your session has expired. Please sign in and try again. Your card has NOT been charged.');
                            }
                        });
                    }
                } else {
                    // No payment required, just continue
                    this.enrollApplication.progress++;
                    this.enrollApplication.is_unpaid = 0;
                    this.enrollApplication.completed_at = new Date();
                    this.payment = false;
                    this.submit();
                }
            },
            scrollTo(refName) {
                let element = this.$refs[refName];
                if (element) {
                    element.scrollIntoView({behavior: 'smooth'});
                }
            },
        },
        mounted() {
            this.load(true);
        },
        destroyed() {
            if (card) {
                card.destroy();
            }
        }
    };
</script>

<style lang="scss" scoped>
    .award:last-child .award-divider {
        display: none;
    }

    .card-container {
        border: 1px solid #e7e7e7;
        border-radius: 8px;
        padding: 2rem;
        margin-bottom: 2rem;
    }

    .errors-container {
        border: 2px solid red;
        border-radius: 8px;
        padding: 1rem 2rem;
        margin-top: 2rem;
    }
</style>

<style lang="scss">
    .block > div, .render-paragraph, .render-header, .field {
        padding-left: 0 !important;
    }

    // Reviewer notes keep their line breaks
    .review-message {
        white-space: pre-line;
    }
</style>
